/**
 * Incremental OAuth for reading a trainee's own spreadsheet.
 *
 * Why this is separate from `googleProvider` in src/lib/firebase.js:
 * adding the Sheets scope to the shared provider would put a new consent
 * screen in front of every existing trainee at their next login, including
 * the ones who only ever do the Sheets Reporting track and never submit a
 * spreadsheet. So the scope is requested lazily, the first time someone
 * actually clicks "Check my sheet".
 *
 * We read the sheet AS the trainee, which means:
 *   - they never have to share the file with anyone,
 *   - no service account is granted standing access to their Drive,
 *   - and we can only ever see what they can already see.
 *
 * The token is held in memory only — never localStorage/sessionStorage. The
 * cost is one extra consent popup after a page reload; the benefit is that a
 * stored OAuth token can't be read back out of the browser later. Because the
 * popup is always triggered by the click on "Check my sheet", it is a genuine
 * user gesture and won't be caught by a popup blocker.
 */

import { GoogleAuthProvider, reauthenticateWithPopup } from 'firebase/auth'
import { auth } from './firebase'

export const SHEETS_SCOPE = 'https://www.googleapis.com/auth/spreadsheets.readonly'

// Google access tokens last an hour. We expire ours early so a check that is
// about to start doesn't die halfway; a 401 from the API is still the
// authoritative signal, handled by clearSheetsToken() + retry.
const ASSUMED_LIFETIME_MS = 55 * 60 * 1000

let cachedToken = null
let cachedUntil = 0
let cachedForUid = null

function buildProvider(email) {
  const provider = new GoogleAuthProvider()
  provider.addScope(SHEETS_SCOPE)
  // login_hint keeps the account picker on the account they're already using.
  if (email) provider.setCustomParameters({ login_hint: email })
  return provider
}

/** Drop the cached token — call this after any 401 from the Sheets API. */
export function clearSheetsToken() {
  cachedToken = null
  cachedUntil = 0
  cachedForUid = null
}

/** True if we hold a token we believe is still good, without prompting. */
export function hasSheetsToken() {
  return Boolean(
    cachedToken && Date.now() < cachedUntil && cachedForUid === auth.currentUser?.uid
  )
}

/**
 * Return an access token for the Sheets API, prompting for consent if we
 * don't already hold a live one.
 *
 * Must be called from a user gesture (a click) the first time, or the popup
 * may be blocked.
 *
 * @throws {Error} with `.code` of 'popup-blocked' | 'popup-closed' |
 *   'wrong-account' | 'no-user' | 'no-token' | 'stale-session'
 */
export async function getSheetsAccessToken() {
  const user = auth.currentUser
  if (!user) {
    throw Object.assign(new Error('You are signed out. Sign in and try again.'), { code: 'no-user' })
  }

  if (hasSheetsToken()) return cachedToken

  const provider = buildProvider(user.email)

  let result
  try {
    // reauthenticateWithPopup pins this to the account already signed in, so a
    // trainee who picks their personal Google account gets a clear error
    // instead of silently switching who the app thinks they are.
    result = await reauthenticateWithPopup(user, provider)
  } catch (err) {
    const code = err?.code || ''
    if (code === 'auth/user-mismatch') {
      throw Object.assign(
        new Error(`That was a different Google account. Choose ${user.email}.`),
        { code: 'wrong-account' }
      )
    }
    if (code === 'auth/popup-blocked') {
      throw Object.assign(
        new Error('Your browser blocked the Google popup. Allow popups for this site and try again.'),
        { code: 'popup-blocked' }
      )
    }
    if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
      throw Object.assign(
        new Error('You closed the Google window before granting access.'),
        { code: 'popup-closed' }
      )
    }
    // There used to be a `signInWithPopup` fallback here for a stale session.
    // It is gone deliberately: signInWithPopup is NOT pinned to the signed-in
    // account (login_hint is only a hint), so a trainee could land on account
    // B — switching the whole Firebase session, sending their progress writes
    // to B's document while they believed they were still A, and caching B's
    // token under A's uid. An unpinned sign-in defeats the one guarantee this
    // module exists to make, so a stale session asks for a fresh sign-in
    // instead.
    if (code === 'auth/user-token-expired' || code === 'auth/requires-recent-login') {
      throw Object.assign(
        new Error('Your sign-in has gone stale. Sign out, sign back in with your Finder account, then check your sheet again.'),
        { code: 'stale-session' }
      )
    }
    throw err
  }

  const credential = GoogleAuthProvider.credentialFromResult(result)
  const token = credential?.accessToken
  if (!token) {
    throw Object.assign(
      new Error('Google did not return access to your spreadsheets. Try again, and make sure you tick the Sheets permission.'),
      { code: 'no-token' }
    )
  }

  cachedToken = token
  cachedUntil = Date.now() + ASSUMED_LIFETIME_MS
  cachedForUid = user.uid
  return token
}
